import { useEffect, useState, type CSSProperties, type ReactNode } from "react";
import { optimizeImage, isDeadImageUrl, imageSrcSet } from "@/lib/image";

interface ProductImageProps {
  src: string | null | undefined;
  alt: string;
  /** When provided, the image is served through the resize/WebP transform endpoint. */
  width?: number;
  /** Intrinsic height attribute; defaults to `width` (product shots are square). */
  height?: number;
  quality?: number;
  className?: string;
  style?: CSSProperties;
  /** Element shown when there is no image or it fails to load. Defaults to a panda placeholder. */
  fallback?: ReactNode;
  draggable?: boolean;
  loading?: "lazy" | "eager";
  /**
   * Fetch priority hint. Set to "high" for the LCP image (e.g. the first
   * product cards of the first above-the-fold row) so the browser downloads it
   * ahead of lazy/low-priority assets.
   */
  fetchPriority?: "high" | "low" | "auto";
  /**
   * Responsive `sizes` hint describing how wide the image actually renders at
   * each breakpoint (e.g. "(min-width:1024px) 16vw, 50vw"). When given, a fuller
   * srcset ladder is emitted so the browser can pick the smallest sufficient
   * file instead of always downloading the 1x/2x of `width`.
   */
  sizes?: string;
  onClick?: (e: React.MouseEvent<HTMLImageElement>) => void;
}

const defaultFallback = (
  <div className="grid h-full w-full place-items-center text-4xl text-muted-foreground">🐼</div>
);

/**
 * Candidate widths (capped at 2x the displayed width) so the browser can
 * choose the smallest file that satisfies the `sizes` hint and device DPR.
 */
function ladderFor(width: number): number[] {
  const cap = width * 2;
  // Small rungs matter on phones: a card that renders ~110 CSS px should be
  // able to pick a 100/120 w file instead of jumping straight to 300 w.
  return [80, 100, 120, 160, 200, 250, 300, 400, 600, 800, 1000, 1200].filter((w) => w <= cap);
}

/**
 * Product image with a graceful fallback. If the source is missing or the
 * image fails to load (e.g. legacy 404s), a panda placeholder is shown instead
 * of a broken image.
 */
export function ProductImage({
  src,
  alt,
  width,
  height,
  quality,
  className,
  style,
  fallback,
  draggable,
  loading = "lazy",
  fetchPriority,
  sizes,
  onClick,
}: ProductImageProps) {
  const [failed, setFailed] = useState(false);
  // Προεπιλεγμένη ποιότητα ανά χρήση, όπως ορίζει το σύστημα εικόνων του OPS:
  // μικρογραφία/κάρτα q=88, μεγάλη εικόνα σελίδας προϊόντος q=92.
  const q = quality ?? (width !== undefined && width > 400 ? 92 : 88);
  const resolved =
    width !== undefined
      ? optimizeImage(src, { width, quality: q })
      : isDeadImageUrl(src)
        ? undefined
        : src ?? undefined;

  // Offer a crisp 2x variant so high-DPR screens get a sharp image while
  // standard screens still download the small 1x file. When a `sizes` hint is
  // given, emit a fuller width ladder so the browser can pick a smaller file.
  const srcSet =
    width !== undefined
      ? imageSrcSet(src, sizes ? ladderFor(width) : [width, width * 2], q)
      : undefined;

  useEffect(() => {
    setFailed(false);
  }, [resolved]);

  if (!src || !resolved || failed) {
    return <>{fallback ?? defaultFallback}</>;
  }

  return (
    <img
      data-product-image=""
      src={resolved}
      srcSet={srcSet}
      sizes={srcSet && width !== undefined ? (sizes ?? `${width}px`) : undefined}
      width={width}
      height={width !== undefined ? (height ?? width) : undefined}
      alt={alt}
      loading={loading}
      decoding="async"
      fetchPriority={fetchPriority}
      draggable={draggable ?? false}
      onClick={onClick}
      onContextMenu={(e) => e.preventDefault()}
      onError={() => setFailed(true)}
      className={className}
      style={{ WebkitTouchCallout: "none", WebkitUserSelect: "none", userSelect: "none", ...style }}
    />
  );
}